/**
 * companyDirectory.js
 *
 * The list of customer companies an internal operator may switch into.
 *
 * The scanline `/customers` endpoint only answers with customers that already
 * have a row in `jobs` (SELECT DISTINCT ... FROM customers c, jobs j WHERE
 * c.id = j.customer_id), so a company onboarded for CorrMonitor, Spectrum XLI
 * or Armadillo Tracking alone — or one whose Cognito group exists before its
 * first CIS job — never appeared in the picker at all.  That is why companies
 * such as "CPC Midstream" went missing.
 *
 * The picker therefore reads four catalogues and merges them:
 *
 *   1. scanline  /customers      — customers with CIS/scanline jobs (legacy list)
 *   2. admin     /rel/customers  — every customer row in Postgres
 *   3. admin     /cog/customers  — every `Customer:<Name>` Cognito group
 *   4. assetview /cp-survey-jobs — customer names used by CP projects
 *
 * Any one of them is enough for a company to be switchable, so a catalogue that
 * fails or is not permitted for this user simply contributes nothing instead of
 * failing the whole lookup.  Names are de-duplicated case- and separator-
 * insensitively (Cognito stores `Customer:CPC_Midstream`, Postgres stores
 * "CPC Midstream"), keeping the most human-readable spelling of each.
 */

import jobListUtils from '../../../../scanline/components/JobsList/utils/utils';
import { listCogCustomers, listPsqlCustomersV2 } from '../../../../admin/api/customers';
import { cacheKey, cachedQuery } from '../../../../scanline/utils/dataCache';
import { normalizeCompanyName } from '../../../../util/user/companySwitch';
import { fetchCpSurveyJobs } from '../../pages/AllProjects/utils/cpSurveyApi';

/** How long a resolved company list is served from cache before revalidating. */
const DIRECTORY_MAX_AGE_MS = 10 * 60 * 1000;

const DIRECTORY_CACHE_KEY = () => cacheKey('company-directory', 'v2');

/**
 * Cognito group names arrive as `Customer:CPC_Midstream`; Postgres and scanline
 * answer with the display name.  Normalise to the display form so the three
 * catalogues can be merged.
 */
const toDisplayName = value =>
	String(value === null || value === undefined ? '' : value)
		.replace(/^Customer:/i, '')
		.replace(/_/g, ' ')
		.replace(/\s+/g, ' ')
		.trim();

/**
 * Prefer the spelling that reads best when two catalogues disagree on case or
 * separators: a name carrying lower-case letters is a real display name rather
 * than an ALL-CAPS or slug form, and a longer one usually carries the full
 * wording ("CPC Midstream" over "CPC").
 */
const preferredSpelling = (current, candidate) => {
	if (!current) {return candidate;}
	const currentMixed = current !== current.toUpperCase();
	const candidateMixed = candidate !== candidate.toUpperCase();
	if (currentMixed !== candidateMixed) {return currentMixed ? current : candidate;}
	return candidate.length > current.length ? candidate : current;
};

/** A catalogue read that resolves to [] instead of rejecting. */
const optional = promise => promise.then(res => res).catch(() => []);

const readScanlineCustomers = () =>
	new Promise(resolve => {
		// Callback-style legacy util; a failure resolves empty so the merge can
		// still proceed on the other catalogues.
		jobListUtils.getCustomers((err, res) => {
			if (err || !res) {
				resolve([]);
				return;
			}
			resolve(Array.isArray(res.customers) ? res.customers : []);
		});
	});

const readPsqlCustomers = () =>
	optional(listPsqlCustomersV2()).then(rows =>
		(Array.isArray(rows) ? rows : []).map(row =>
			row && typeof row === 'object' ? row.name : row
		)
	);

const readCognitoCustomers = () =>
	optional(listCogCustomers()).then(rows =>
		(Array.isArray(rows) ? rows : []).map(row =>
			row && typeof row === 'object' ? row.name || row.groupName : row
		)
	);

/**
 * Merge the catalogues into one sorted, de-duplicated list of display names.
 * Exported for testing — `fetchCompanyDirectory` is what callers use.
 */
export const mergeCompanyNames = (...lists) => {
	const byKey = new Map();
	lists.forEach(list => {
		(Array.isArray(list) ? list : []).forEach(raw => {
			const name = toDisplayName(raw);
			if (!name) {return;}
			const key = normalizeCompanyName(name);
			if (!key) {return;}
			byKey.set(key, preferredSpelling(byKey.get(key), name));
		});
	});
	return [...byKey.values()].sort((a, b) => a.localeCompare(b));
};

// In-flight lookup, shared between the picker and the sidebar's "is there more
// than one company" check so a cold start issues one set of requests, not two.
let inFlight = null;

/**
 * Every company the operator can switch into, cache-first.
 *
 * @param {object}   [options]
 * @param {Function} [options.onCached] Invoked with the cached list (if any) so
 *   the picker can paint immediately, before the revalidated list resolves.
 * @returns {Promise<string[]>} Sorted display names.
 */
export const fetchCompanyDirectory = ({ onCached } = {}) => {
	if (inFlight) {
		return onCached
			? inFlight.then(list => { if (list.length > 0) {onCached(list);} return list; })
			: inFlight;
	}
	const request = cachedQuery({
		key: DIRECTORY_CACHE_KEY(),
		maxAgeMs: DIRECTORY_MAX_AGE_MS,
		onCached: cached => {
			if (onCached && Array.isArray(cached) && cached.length > 0) {
				onCached(cached);
			}
		},
		fetcher: () =>
			Promise.all([
				readScanlineCustomers(),
				readPsqlCustomers(),
				readCognitoCustomers(),
				optional(fetchCpSurveyJobs()).then(jobs => jobs.map(job => job.customer))
			]).then(lists => mergeCompanyNames(...lists))
	})
		.then(list => (Array.isArray(list) ? list : []))
		.finally(() => { inFlight = null; });

	inFlight = request;
	return request;
};

export default fetchCompanyDirectory;
